import { API_ENDPOINTS } from '../config/api'
import { withOrganizationQuery } from '../utils/orgUtils'

function authHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

export type ScannerProviderLifecycle = {
  supported: boolean
  configured: boolean
  enabled: boolean
  healthy: boolean
  launchable: boolean
  status: string
  statusMessage?: string | null
  disabledReason?: string | null
  nextAction?: string | null
}

export type ScannerProviderRow = {
  id: string
  providerType: string
  displayName: string
  enabled: boolean
  endpointUrl?: string | null
  status: string
  statusMessage?: string | null
  lastHealthCheckAt?: string | null
  lastScanAt?: string | null
  configJson?: Record<string, unknown>
  selectable?: boolean
  disabledReason?: string | null
  hasSecret?: boolean
  lifecycle?: ScannerProviderLifecycle
  scanTypes?: Array<{ id: string; label: string; disabled?: boolean }>
}

export async function fetchScannerProviders(token: string) {
  const res = await fetch(withOrganizationQuery(API_ENDPOINTS.SCANNER_GATEWAY_PROVIDERS), {
    headers: authHeaders(token),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.message || 'Failed to load scanner providers')
  return (json.data?.providers || []) as ScannerProviderRow[]
}

export async function testScannerProvider(token: string, providerId: string) {
  const res = await fetch(withOrganizationQuery(API_ENDPOINTS.SCANNER_GATEWAY_PROVIDER_TEST(providerId)), {
    method: 'POST',
    headers: authHeaders(token),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.message || 'Health check failed')
  return json.data?.provider as ScannerProviderRow
}

export type ScannerScanRow = {
  id: string
  providerType: string
  target: string
  scanType: string
  status: string
  assetId?: string | null
  informationSystemId?: string | null
  errorMessage?: string | null
  parserStatus?: string | null
  createdAt: string
  startedAt?: string | null
  completedAt?: string | null
  summary?: Record<string, unknown> | null
  summaryJson?: Record<string, unknown> | null
  findingCount?: number
  postureGovernance?: {
    assessmentAt: string
    governsCurrentPosture: boolean
    postureRole: 'CURRENT' | 'HISTORICAL'
    assessmentFindingCount?: number
    assessmentSeverityCounts?: { low?: number; medium?: number; high?: number; critical?: number; info?: number }
  } | null
}

export type ScannerFindingRow = {
  id: string
  title: string
  severity: string
  cwe?: string | null
  cve?: string | null
  host?: string | null
  url?: string | null
  remediation?: string | null
  controlImpacts?: Array<{ standardCode: string; controlKey: string; confidence: string }>
}

export type ParsedReportPayload = {
  scan: ScannerScanRow
  summary: Record<string, unknown>
  /** @deprecated Prefer assessmentScope / assessment from three-scope DTO */
  assessment?: Record<string, unknown> | null
  assessmentCoverage?: Record<string, unknown> | null
  assessmentScope?: Record<string, unknown> | null
  currentPostureContext?: Record<string, unknown> | null
  historicalContext?: Record<string, unknown> | null
  assessmentId?: string | null
  findings: ScannerFindingRow[]
  observations: Array<{
    id: string
    title: string
    recommendation?: string | null
    severity: string
  }>
  enabledStandards: string[]
  remediation: Array<{
    id: string
    title: string
    recommendations?: string[]
    requiredEvidence?: string[]
  }>
  downloads: {
    xml?: { available: boolean; path?: string | null; size?: number | null; checksum?: string | null; generatedAt?: string | null }
    json?: { available: boolean; path?: string | null; size?: number | null; checksum?: string | null; generatedAt?: string | null }
    jsonl?: { available: boolean; path?: string | null; size?: number | null; checksum?: string | null; generatedAt?: string | null }
    pdf?: { available: boolean; path?: string | null; size?: number | null; checksum?: string | null; generatedAt?: string | null }
    html?: { available: boolean; path?: string | null; size?: number | null; checksum?: string | null; generatedAt?: string | null }
    raw?: { available: boolean; path?: string | null; size?: number | null; checksum?: string | null; generatedAt?: string | null }
  }
  parseFailed?: boolean
  parseError?: string | null
}

export type AssetLinkageDiagnostics = {
  assetId: string
  assetFound: boolean
  informationSystemIds: string[]
  enabledStandards: string[]
  issues: string[]
  readyForControlMapping: boolean
}

export type ScanDownloadInfo = {
  pdf?: { available: boolean; url?: string | null; error?: string | null }
  xml?: { available: boolean; url?: string | null; error?: string | null }
}

export type GreenboneReportDiagnostics = {
  scanFound: boolean
  scanId: string
  scanStatus?: string
  taskId?: string
  reportId?: string
  pdfAvailable?: boolean
  xmlAvailable?: boolean
  availableFormats?: Array<{ report_format_id: string; name: string; extension: string }>
  errors?: string[]
  suggestedFix?: string[]
}

export async function patchScannerProvider(
  token: string,
  providerId: string,
  body: Partial<
    Pick<ScannerProviderRow, 'enabled' | 'endpointUrl' | 'displayName'> & {
      apiKey?: string
      username?: string
      authType?: string
    }
  >,
) {
  const res = await fetch(withOrganizationQuery(API_ENDPOINTS.SCANNER_GATEWAY_PROVIDER(providerId)), {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.message || 'Update failed')
  return json.data?.provider as ScannerProviderRow
}

export async function fetchLaunchOptions(token: string) {
  const res = await fetch(withOrganizationQuery(API_ENDPOINTS.SCANNER_GATEWAY_LAUNCH_OPTIONS), {
    headers: authHeaders(token),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.message || 'Failed to load launch options')
  return (json.data?.providers || []) as ScannerProviderRow[]
}

export async function startGatewayScan(
  token: string,
  body: {
    providerConfigId: string
    assetId?: string
    target?: string
    scanType: string
    confirmScan: boolean
  },
) {
  const res = await fetch(withOrganizationQuery(API_ENDPOINTS.SCANNER_GATEWAY_SCAN_START), {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.message || 'Failed to start scan')
  return json.data?.scan
}

export async function fetchScannerScans(token: string, providerConfigId?: string) {
  const base = providerConfigId
    ? `${API_ENDPOINTS.SCANNER_GATEWAY_SCANS}?providerConfigId=${encodeURIComponent(providerConfigId)}`
    : API_ENDPOINTS.SCANNER_GATEWAY_SCANS
  const res = await fetch(withOrganizationQuery(base), {
    headers: authHeaders(token),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.message || 'Failed to load scans')
  return (json.data?.scans || []) as ScannerScanRow[]
}

export async function reconcileScannerScans(token: string, body: { limit?: number; providerType?: string } = {}) {
  const res = await fetch(withOrganizationQuery(API_ENDPOINTS.SCANNER_GATEWAY_SCANS_RECONCILE), {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.message || 'Reconcile failed')
  return json.data as { reconciled: number }
}

export async function fetchScanFindings(token: string, scanId: string) {
  const res = await fetch(withOrganizationQuery(API_ENDPOINTS.SCANNER_GATEWAY_SCAN_FINDINGS(scanId)), {
    headers: authHeaders(token),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.message || 'Failed to load findings')
  return json.data as { scan: ScannerScanRow; findings: ScannerFindingRow[] }
}

export async function fetchParsedReport(token: string, scanId: string) {
  const res = await fetch(withOrganizationQuery(API_ENDPOINTS.SCANNER_GATEWAY_SCAN_PARSED_REPORT(scanId)), {
    headers: authHeaders(token),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.message || 'Failed to load parsed report')
  return json.data as ParsedReportPayload
}

export async function runAssetLinkageDiagnostics(token: string, assetId: string) {
  const res = await fetch(
    withOrganizationQuery(API_ENDPOINTS.SCANNER_GATEWAY_ASSET_LINKAGE_DIAGNOSTICS(assetId)),
    {
      headers: authHeaders(token),
    },
  )
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.message || 'Diagnostics failed')
  return json.data as AssetLinkageDiagnostics
}

export async function downloadScannerReport(token: string, scanId: string, format: string) {
  const res = await fetch(withOrganizationQuery(API_ENDPOINTS.SCANNER_GATEWAY_SCAN_DOWNLOAD(scanId, format)), {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) {
    const json = await res.json().catch(() => ({}))
    throw new Error(json.message || 'Download failed')
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `scan-${scanId}.${format}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function runGreenboneReportDiagnostics(token: string, scanId: string) {
  const res = await fetch(
    withOrganizationQuery(API_ENDPOINTS.SCANNER_GATEWAY_GREENBONE_REPORT_DIAGNOSTICS(scanId)),
    {
      headers: authHeaders(token),
    },
  )
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.message || 'Report diagnostics failed')
  return json.data as GreenboneReportDiagnostics
}
